import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

import ScrollIndicator from "./components/ScrollIndicator";

// Pages
import LandingPage from "./pages/LandingPage";
import PropertiesPage from "./pages/PropertiesPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import ComparePropertiesPage from "./pages/ComparePropertiesPage";
import AgentsPage from "./pages/AgentsPage";
import DashboardPage from "./pages/DashboardPage";
import MortgageCalculator from "./pages/MortgageCalculator";
import AuthCallback from "./pages/AuthCallback";
import TenantMaintenancePortal from "./pages/TenantMaintenancePortal";
import LandlordMaintenanceAuth from "./pages/LandlordMaintenanceAuth";
import BrokerConfirmation from "./pages/BrokerConfirmation";
import BrokerApplicationPage from "./pages/BrokerApplicationPage";

// Determine backend URL - use current origin for production deployments
const getBackendUrl = () => {
  // If we're on an emergent.host domain, use the same origin
  if (window.location.hostname.endsWith('.emergent.host')) {
    return window.location.origin;
  }
  // Otherwise use the environment variable (for preview/development)
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Send cookies (auth session) with all axios requests by default
axios.defaults.withCredentials = true;

// Setup axios interceptor to handle auth errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't intercept errors during the auth callback process
    if (window.location.pathname === '/auth/callback') {
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401) {
      // Session expired or invalid - clear and redirect to login
      console.log("Session expired, clearing auth state");
      sessionStorage.clear();
      // Don't redirect if already on public page
      if (window.location.pathname.startsWith('/dashboard')) {
        toast.error("Session expired. Please sign in again.");
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      }
    } else if (error.response?.status === 403) {
      // Permission denied - might need to refresh user data
      console.log("Permission denied - you may need to sign out and back in");
      // Only show toast if it's not an auth-related 403 (those are handled in AuthCallback)
      if (!error.config?.url?.includes('/auth/')) {
        toast.error("Permission denied. Try signing out and back in.");
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Helper to get auth headers (for browsers that block cookies)
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Setup axios to always include auth token from localStorage as fallback
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authCheckRef = useRef(false);

  useEffect(() => {
    // Prevent double checking in strict mode
    if (authCheckRef.current) return;
    authCheckRef.current = true;
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders(),
        timeout: 10000 // 10 second timeout for slow mobile connections
      });
      setUser(response.data);
    } catch (error) {
      console.log("Auth check failed:", error.message);
      // Clear any stale token
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // Clear any stale auth data before login
    sessionStorage.clear();
    localStorage.removeItem('session_token');
    localStorage.removeItem('pendingAgentApplication');
    
    // Store intended destination for mobile browsers
    sessionStorage.setItem('authRedirectPath', window.location.pathname);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear all auth-related storage
    setUser(null);
    sessionStorage.clear();
    localStorage.removeItem('session_token');
    localStorage.removeItem('pendingAgentApplication');
    
    // Force reload to clear any cached state
    window.location.href = "/";
  };

  // Force refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        timeout: 10000
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.log("User refresh failed:", error.message);
      setUser(null);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading, login } = useAuth();
  const location = useLocation();

  // If user data was passed from AuthCallback, use it
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8">
          <h2 className="text-2xl font-serif mb-4">Sign In Required</h2>
          <p className="text-muted-foreground mb-6">Please sign in to access the dashboard</p>
          <button
            onClick={login}
            className="bg-primary text-primary-foreground px-8 py-3 rounded-sm hover:bg-primary/90 transition-colors"
          >
            Sign In with Google
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/properties" element={<PropertiesPage />} />
      <Route path="/properties/compare" element={<ComparePropertiesPage />} />
      <Route path="/properties/:id" element={<PropertyDetailPage />} />
      <Route path="/agents" element={<AgentsPage />} />
      <Route path="/mortgage-calculator" element={<MortgageCalculator />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/maintenance/:token" element={<TenantMaintenancePortal />} />
      <Route path="/maintenance-auth/:token" element={<LandlordMaintenanceAuth />} />
      <Route path="/broker-confirm/:token" element={<BrokerConfirmation />} />
      <Route path="/apply/broker" element={<BrokerApplicationPage />} />
      <Route path="/dashboard/*" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppRouter />
          <ScrollIndicator />
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
